import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowSquareOut, Briefcase, ClipboardText, Hammer, Lock, Printer, Truck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { HoldLabelPrint, holdFromSlab } from "@/components/HoldLabelPrint";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const OPEN_PO = ["Draft", "Ordered", "Partial"];
const OPEN_QUOTE = ["Draft", "Sent", "Viewed", "Accepted"];

const Row = ({ to, testId, title, sub, right, tone = "zinc" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 transition-colors duration-150 ${
      tone === "amber"
        ? "border-amber-500/30 bg-amber-500/5 hover:border-amber-400/60"
        : tone === "indigo"
          ? "border-indigo-500/30 bg-indigo-500/5 hover:border-indigo-400/60"
          : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
    }`}
  >
    <div className="min-w-0">
      <div className="truncate text-sm font-semibold text-zinc-100">{title}</div>
      {sub && <div className="truncate text-xs text-zinc-500">{sub}</div>}
    </div>
    <div className="flex shrink-0 items-center gap-2 text-xs text-zinc-400">
      {right}
      <ArrowSquareOut size={13} weight="bold" />
    </div>
  </Link>
);

const SectionTrigger = ({ icon: Icon, label, count, testId }) => (
  <AccordionTrigger
    data-testid={testId}
    className="py-3 text-[10px] uppercase tracking-widest text-zinc-400 hover:no-underline [&[data-state=open]>svg]:text-zinc-300"
  >
    <span className="flex items-center gap-2">
      <Icon size={13} weight="bold" /> {label}
      <span className="font-mono-data text-zinc-600">{count}</span>
    </span>
  </AccordionTrigger>
);

// Where this stock item is spoken for: projects, purchase orders, quotes, holds and
// jobs — each section is a collapsible accordion and every record links out.
export const ItemLinks = ({ kind, itemId, item, testId = "item-links" }) => {
  const [links, setLinks] = useState(null);
  const [printing, setPrinting] = useState(null);

  useEffect(() => {
    if (!itemId) return;
    api
      .get(`/inventory/${kind}/${itemId}/links`)
      .then(({ data }) => setLinks(data))
      .catch(() => setLinks({ purchase_orders: [], quotes: [], reservations: [], jobs: [], projects: [] }));
  }, [kind, itemId, item?.updated_at]);

  if (!links) return null;
  const pos = links.purchase_orders || [];
  const quotes = links.quotes || [];
  const holds = links.reservations || [];
  const jobs = links.jobs || [];
  const projects = links.projects || [];
  const openPos = pos.filter((p) => OPEN_PO.includes(p.status));
  const openQuotes = quotes.filter((q) => OPEN_QUOTE.includes(q.status));
  const total = pos.length + quotes.length + holds.length + jobs.length + projects.length;

  const printFor = (p) => {
    if (!item) return;
    const quoteCodes = p.quote_codes || [];
    const pieces = (item.pieces || []).filter(
      (pc) => pc.status === "Reserved" && (pc.held_project_id === p.id || quoteCodes.includes(pc.held_for)),
    );
    const list = pieces.length ? pieces.map((pc) => holdFromSlab(item, pc)) : [holdFromSlab(item)];
    setPrinting({
      holds: list,
      context: {
        customer: p.customer_name,
        title: p.name,
        code: p.code,
        address: p.job_address,
        project_id: p.id,
        quote_code: quoteCodes.join(", "),
        held_at: p.held_at,
        held_by: p.held_by,
        install_date: p.target_install_date,
      },
    });
  };

  const openSections = [
    projects.length && "projects",
    pos.length && "pos",
    quotes.length && "quotes",
    holds.length && "holds",
    jobs.length && "jobs",
  ].filter(Boolean);

  return (
    <div
      className="min-w-0 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">On order &amp; quoted</div>
        <div className="flex flex-wrap gap-1.5">
          {openPos.length > 0 && (
            <span
              data-testid={`${testId}-po-badge`}
              className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300"
            >
              On {openPos.length === 1 ? openPos[0].code : `${openPos.length} POs`}
            </span>
          )}
          {openQuotes.length > 0 && (
            <span
              data-testid={`${testId}-quote-badge`}
              className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-300"
            >
              Quoted ×{openQuotes.length}
            </span>
          )}
          {holds.length > 0 && (
            <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300">
              On hold ×{holds.length}
            </span>
          )}
          {projects.some((p) => p.pieces > 0) && (
            <span
              data-testid={`${testId}-reserved-badge`}
              className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300"
            >
              Reserved ×{projects.reduce((n, p) => n + (p.pieces || 0), 0)}
            </span>
          )}
        </div>
      </div>

      {total === 0 ? (
        <p className="mt-3 text-sm text-zinc-500" data-testid={`${testId}-empty`}>
          Not on any project, purchase order, quote, hold or job.
        </p>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={openSections}
          className="mt-2 divide-y divide-zinc-800"
          data-testid={`${testId}-accordion`}
        >
          {projects.length > 0 && (
            <AccordionItem value="projects" className="border-zinc-800" data-testid={`${testId}-projects`}>
              <SectionTrigger
                icon={Briefcase}
                label="Assigned to projects"
                count={projects.length}
                testId={`${testId}-projects-trigger`}
              />
              <AccordionContent className="space-y-2">
                {projects.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`${testId}-project-${p.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5"
                  >
                    <Link
                      to={`/projects/${p.id}`}
                      data-testid={`${testId}-project-${p.id}-link`}
                      className="min-w-0 flex-1 hover:underline"
                    >
                      <div className="truncate text-sm font-semibold text-zinc-100">
                        <span className="font-mono-data text-amber-300">{p.code}</span> · {p.name}
                      </div>
                      <div className="truncate text-xs text-zinc-500">
                        {[
                          p.customer_name,
                          p.pieces > 0
                            ? `${p.pieces} piece${p.pieces > 1 ? "s" : ""} reserved`
                            : "quoted, nothing held yet",
                          p.quote_codes?.length ? p.quote_codes.join(", ") : "",
                          p.target_install_date ? `install ${p.target_install_date}` : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </Link>
                    <div className="flex shrink-0 items-center gap-2">
                      {item && (
                        <button
                          type="button"
                          data-testid={`${testId}-project-${p.id}-print-hold`}
                          onClick={() => printFor(p)}
                          title="Print a 4x6 HOLD label for this project"
                          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-amber-500/40 px-2.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20"
                        >
                          <Printer size={13} weight="bold" /> Hold label
                        </button>
                      )}
                      <Link
                        to={`/projects/${p.id}`}
                        data-testid={`${testId}-project-${p.id}-open`}
                        title="Open project"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200"
                      >
                        <ArrowSquareOut size={13} weight="bold" />
                      </Link>
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}

          {pos.length > 0 && (
            <AccordionItem value="pos" className="border-zinc-800">
              <SectionTrigger
                icon={Truck}
                label="Purchase orders"
                count={pos.length}
                testId={`${testId}-pos-trigger`}
              />
              <AccordionContent className="space-y-2">
                {pos.map((p) => (
                  <Row
                    key={p.id}
                    to={`/inventory/purchase-orders/${p.id}`}
                    testId={`${testId}-po-${p.id}`}
                    tone={OPEN_PO.includes(p.status) ? "amber" : "zinc"}
                    title={`${p.code} · ${p.supplier_name || "No supplier"}`}
                    sub={[
                      `${p.received_qty}/${p.qty} received`,
                      p.expected_date ? `expected ${p.expected_date}` : "",
                      p.supplier_confirmation?.ship_date ? `ships ${p.supplier_confirmation.ship_date}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    right={<span className="uppercase tracking-widest">{p.status}</span>}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          )}

          {quotes.length > 0 && (
            <AccordionItem value="quotes" className="border-zinc-800">
              <SectionTrigger
                icon={ClipboardText}
                label="Quotes"
                count={quotes.length}
                testId={`${testId}-quotes-trigger`}
              />
              <AccordionContent className="space-y-2">
                {quotes.map((q) => (
                  <Row
                    key={q.id}
                    to={`/quotes/${q.id}`}
                    testId={`${testId}-quote-${q.id}`}
                    tone={OPEN_QUOTE.includes(q.status) ? "indigo" : "zinc"}
                    title={`${q.code} · ${q.project_name || q.customer_name}`}
                    sub={[
                      q.customer_name,
                      q.match === "color" ? "color match" : "this item on the quote",
                      q.updated_at ? q.updated_at.slice(0, 10) : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    right={<span className="uppercase tracking-widest">{q.status}</span>}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          )}

          {holds.length > 0 && (
            <AccordionItem value="holds" className="border-zinc-800">
              <SectionTrigger
                icon={Lock}
                label="Material on hold"
                count={holds.length}
                testId={`${testId}-holds-trigger`}
              />
              <AccordionContent className="space-y-2">
                {holds.map((h) => (
                  <Row
                    key={h.id}
                    to={`/reservation/${h.id}`}
                    testId={`${testId}-hold-${h.id}`}
                    title={`${h.tag || "Hold"} · ${h.customer_name}`}
                    sub={[h.project_name, h.install_date ? `install ${h.install_date}` : ""]
                      .filter(Boolean)
                      .join(" · ")}
                    right={<span className="uppercase tracking-widest">{h.status}</span>}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          )}

          {jobs.length > 0 && (
            <AccordionItem value="jobs" className="border-zinc-800">
              <SectionTrigger
                icon={Hammer}
                label="Jobs"
                count={jobs.length}
                testId={`${testId}-jobs-trigger`}
              />
              <AccordionContent className="space-y-2">
                {jobs.map((j) => (
                  <Row
                    key={j.id}
                    to={`/projects/${j.id}?tab=field`}
                    testId={`${testId}-job-${j.id}`}
                    title={`${j.code} · ${j.project_name || j.customer_name}`}
                    sub={j.customer_name}
                    right={<span className="uppercase tracking-widest">{j.stage || j.status}</span>}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}

      {printing && (
        <HoldLabelPrint
          holds={printing.holds}
          context={printing.context}
          onClose={() => setPrinting(null)}
          testId={`${testId}-hold-label`}
        />
      )}
    </div>
  );
};
